import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, X, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Button, Field, Input, Modal, Textarea } from "@/components/ui-kit";
import { parseQty, formatQty } from "@/lib/format";

/**
 * Damage Report Modal — Pelaporan Material Rusak
 * - Wajib upload foto (kamera atau galeri)
 * - Foto otomatis tercantum tanggal & waktu (photo_taken_at = saat tombol Pilih Foto/Kamera dipakai)
 * - Wajib isi kronologi kejadian
 * - Saat submit: backend POST /api/damage-reports → kurangi stok + naikkan damaged_qty + log ke StockLog
 */
export function DamageReportModal({ open, onClose, stock, onSuccess }) {
  const [qty, setQty] = useState("");
  const [chronology, setChronology] = useState("");
  const [reportType, setReportType] = useState("damage"); // "damage" | "lost"
  const [photo, setPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputCamera = useRef(null);
  const fileInputGallery = useRef(null);

  const reset = () => {
    setQty("");
    setChronology("");
    setPhoto(null);
    setReportType("damage");
  };

  const handleClose = () => {
    if (submitting || uploading) return;
    reset();
    onClose?.();
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "damage");
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const takenAt = new Date().toISOString();
      setPhoto({
        id: r.data.id,
        path: r.data.path,
        filename: r.data.filename,
        content_type: r.data.content_type,
        taken_at: takenAt,
      });
      toast.success("Foto kerusakan terunggah");
    } catch (err) {
      toast.error("Gagal upload foto");
    } finally {
      setUploading(false);
      // Reset file inputs so the same file can be re-selected if needed
      if (fileInputCamera.current) fileInputCamera.current.value = "";
      if (fileInputGallery.current) fileInputGallery.current.value = "";
    }
  };

  const removePhoto = () => setPhoto(null);

  const submit = async () => {
    const qtyNum = parseQty(qty);
    if (!qtyNum || qtyNum <= 0) {
      toast.error("Isi jumlah material rusak");
      return;
    }
    if (qtyNum > parseFloat(stock?.quantity || 0)) {
      toast.error("Jumlah rusak melebihi stok yang tersedia");
      return;
    }
    if (!chronology.trim()) {
      toast.error("Kronologi kejadian wajib diisi");
      return;
    }
    if (!photo) {
      toast.error("Foto material rusak wajib diunggah");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/damage-reports", {
        stock_id: stock.id,
        quantity: qtyNum,
        chronology: chronology.trim(),
        report_type: reportType,
        photo_file_id: photo.id,
        photo_file_path: photo.path,
        photo_filename: photo.filename,
        photo_content_type: photo.content_type,
        photo_taken_at: photo.taken_at,
      });
      toast.success(reportType === "lost" ? "Laporan stok hilang tersimpan" : "Laporan kerusakan tersimpan");
      reset();
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengirim laporan");
    } finally {
      setSubmitting(false);
    }
  };

  if (!stock) return null;

  return (
    <Modal open={open} onClose={handleClose} title={reportType === "lost" ? "Lapor Stok Hilang" : "Lapor Material Rusak"} size="lg">
      <div className="space-y-4">
        <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
          <div><b>{stock.material_name}</b> {stock.code ? <span className="font-mono">[{stock.code}]</span> : null}</div>
          <div className="text-[11px]">Stok tersedia: <b>{formatQty(stock.quantity)} {stock.unit}</b> · Rusak bulan ini: <b>{formatQty(stock.damaged_qty || 0)}</b> · Hilang bulan ini: <b>{formatQty(stock.lost_qty || 0)} {stock.unit}</b></div>
        </div>

        <Field label="Jenis Laporan" required>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            className="w-full border border-stone-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-terra-500"
            data-testid="damage-report-type"
          >
            <option value="damage">Stok Rusak</option>
            <option value="lost">Stok Hilang</option>
          </select>
        </Field>

        <Field label={reportType === "lost" ? "Jumlah Stok Hilang" : "Jumlah Material Rusak"} required>
          <Input
            className="font-mono-num"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            placeholder="0"
            data-testid="damage-qty-input"
          />
        </Field>

        <Field label="Kronologi Kejadian" required hint="Jelaskan apa yang terjadi, kapan, dimana, dan penyebabnya">
          <Textarea
            rows={3}
            value={chronology}
            onChange={(e) => setChronology(e.target.value)}
            placeholder={reportType === "lost"
              ? "Contoh: Stok tidak ditemukan saat opname tanggal 12, kemungkinan hilang saat bongkar muat…"
              : "Contoh: Material jatuh saat bongkar muat di gudang pada 12.30 WIB, sebagian terjatuh dan retak…"}
            data-testid="damage-chronology-input"
          />
        </Field>

        <div>
          <label className="block text-xs uppercase tracking-wider font-semibold text-stone-600 mb-1">{reportType === "lost" ? "Foto Lokasi (opsional bisa stok terakhir terlihat)" : "Foto Material Rusak"} <span className="text-red-600">*</span></label>
          <p className="text-[11px] text-stone-500 mb-2">Wajib unggah foto. Bisa langsung dari kamera perangkat atau pilih dari galeri.</p>
          {photo ? (
            <div className="border border-stone-200 p-3 flex items-start gap-3" data-testid="damage-photo-preview">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${photo.id}`}
                alt="Foto kerusakan"
                className="w-32 h-32 object-cover border border-stone-200"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <div className="flex-1 text-xs">
                <div className="font-mono break-all text-stone-700">{photo.filename}</div>
                <div className="text-stone-500 mt-1">Diambil: <span className="font-mono">{new Date(photo.taken_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", hour12: false })} WIB</span></div>
                <button
                  type="button"
                  onClick={removePhoto}
                  className="mt-2 inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-[11px]"
                  data-testid="damage-photo-remove"
                >
                  <Trash2 className="h-3 w-3" /> Hapus foto
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2 flex-wrap">
              <label className="inline-flex items-center gap-2 px-3 py-2 border border-stone-300 cursor-pointer hover:bg-stone-50 text-sm" data-testid="damage-photo-camera-btn">
                <Camera className="h-4 w-4" /> {uploading ? "Mengunggah…" : "Ambil Foto (Kamera)"}
                <input
                  ref={fileInputCamera}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleUpload}
                  className="hidden"
                  data-testid="damage-photo-camera-input"
                />
              </label>
              <label className="inline-flex items-center gap-2 px-3 py-2 border border-stone-300 cursor-pointer hover:bg-stone-50 text-sm" data-testid="damage-photo-gallery-btn">
                Pilih dari Galeri
                <input
                  ref={fileInputGallery}
                  type="file"
                  accept="image/*"
                  onChange={handleUpload}
                  className="hidden"
                  data-testid="damage-photo-gallery-input"
                />
              </label>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-stone-200">
          <Button variant="ghost" onClick={handleClose} disabled={submitting}>Batal</Button>
          <Button onClick={submit} disabled={submitting || uploading} data-testid="damage-submit-btn">
            {submitting ? "Mengirim…" : "Kirim Laporan"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
